import { useEffect, useMemo, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Search, ImageOff, Package } from "lucide-react";
import { toast } from "sonner";
import ProductForm from "@/components/ProductForm";

export default function Products() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const params = {};
    if (search) params.search = search;
    if (categoryId) params.category_id = categoryId;
    const { data } = await api.get("/products", { params });
    setItems(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, categoryId]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  function onAdd() { setEditing(null); setOpen(true); }
  function onEdit(p) { setEditing(p); setOpen(true); }
  async function onDelete(p) {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="products-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ CATALOG · ALL TEES & POLOS</div>
          <h1 className="section-title text-5xl lg:text-6xl">Products.</h1>
        </div>
        <button data-testid="add-product-btn" onClick={onAdd} className="brut-btn px-5 py-3 inline-flex items-center gap-2 self-start">
          <Plus size={16} /> New Product
        </button>
      </header>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="products-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or SKU…"
            className="brut-input pl-10"
          />
        </div>
        <select
          data-testid="products-category-filter"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="brut-input lg:w-64"
        >
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="brut-card p-12 text-center" data-testid="products-empty-state">
          <div className="brut-stripes h-16 w-16 mx-auto mb-4" />
          <Package size={40} className="mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display font-black text-2xl mb-2">No products yet</h3>
          <p className="text-zinc-600 mb-5 text-sm">Drop your first 240 GSM hero into the catalog.</p>
          <button onClick={onAdd} className="brut-btn px-5 py-3 inline-flex items-center gap-2">
            <Plus size={16} /> Create First Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((p) => (
            <ProductCard key={p.id} p={p} categories={categories} onEdit={() => onEdit(p)} onDelete={() => onDelete(p)} />
          ))}
        </div>
      )}

      {open && (
        <ProductForm
          product={editing}
          categories={categories}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductCard({ p, categories, onEdit, onDelete }) {
  const cat = useMemo(() => categories.find((c) => c.id === p.category_id), [categories, p.category_id]);
  const img = p.images?.[0];
  return (
    <div className="brut-card overflow-hidden brut-fade" data-testid={`product-card-${p.sku}`}>
      <Link to={`/products/${p.id}`} className="block aspect-square bg-zinc-100 border-b-2 border-black relative">
        {img ? (
          <img src={fileUrl(img)} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-400">
            <ImageOff size={32} />
          </div>
        )}
        <div className="absolute top-3 left-3">
          {p.is_low_stock && p.total_stock > 0 && <span className="brut-badge warning">LOW</span>}
          {p.total_stock === 0 && <span className="brut-badge alert">OOS</span>}
        </div>
      </Link>
      <div className="p-4">
        <div className="font-mono-ds text-[10px] text-zinc-500 tracking-widest uppercase">{cat?.name || "—"}</div>
        <Link to={`/products/${p.id}`} className="block font-display font-black text-base leading-tight mt-1 hover:underline">{p.name}</Link>
        <div className="font-mono-ds text-[11px] text-zinc-500 mt-1">{p.sku}</div>
        <div className="flex items-end justify-between mt-3">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">PRICE</div>
            <div className="font-mono-ds font-bold text-lg">₹{Number(p.price).toLocaleString("en-IN")}</div>
          </div>
          <div className="text-right">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">STOCK</div>
            <div className="font-mono-ds font-bold text-lg">{p.total_stock}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onEdit} className="brut-btn-outline flex-1 py-2 text-xs" data-testid={`edit-product-${p.sku}`}>Edit</button>
          <button onClick={onDelete} className="brut-btn-outline py-2 px-3 text-xs hover:bg-[#FF3333] hover:text-white" data-testid={`delete-product-${p.sku}`}>✕</button>
        </div>
      </div>
    </div>
  );
}
